import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { alarm as alarmApi } from "@/endpoints/dryp/alarm";
import type { Alarm } from "../types";

interface AlarmActiveSwitchProps {
	alarm: Alarm;
	canToggle: boolean;
	onToggled?: () => void | Promise<void>;
}

export const AlarmActiveSwitch = observer(({ alarm, canToggle, onToggled }: AlarmActiveSwitchProps) => {
	// Optimistic target value. While set, the switch displays it instead of
	// `alarm.enabled`, so a slow/cached list refetch can't make the toggle snap
	// back. Cleared once the alarm prop catches up, or on API error (revert).
	const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);
	const [requesting, setRequesting] = useState(false);

	useEffect(() => {
		if (pendingEnabled !== null && alarm.enabled === pendingEnabled) {
			setPendingEnabled(null);
		}
	}, [alarm.enabled, pendingEnabled]);

	const handleChange = async (enabled: boolean) => {
		setPendingEnabled(enabled);
		setRequesting(true);
		try {
			await alarmApi.update(Number(alarm.id), { enabled });
		} catch (error) {
			const apiError = error as ApiError;
			toast.error(apiError?.message || `Failed to update alarm "${alarm.name}". Please try again.`);
			setPendingEnabled(null);
			setRequesting(false);
			return;
		}
		toast.success(`Alarm "${alarm.name}" ${enabled ? "enabled" : "disabled"}`);
		setRequesting(false);
		// The toggle itself succeeded — a refetch failure should not surface as a
		// "Failed to update" toast (the alarm IS updated). Swallow refetch errors.
		try {
			await onToggled?.();
		} catch {
			// noop
		}
	};

	const checked = pendingEnabled ?? alarm.enabled;
	const loading = requesting;

	if (loading) {
		return (
			<div className="inline-flex h-5 w-9 items-center" aria-busy="true" aria-live="polite">
				<div className="relative h-1 w-full overflow-hidden rounded-full bg-primary/10">
					<div className="absolute inset-y-0 w-2/5 animate-indeterminate-progress rounded-full bg-primary" />
				</div>
			</div>
		);
	}

	if (!canToggle) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					{/* span wrapper so the tooltip still receives hover when the inner Switch is disabled */}
					<span className="inline-flex" tabIndex={0}>
						<Switch checked={checked} disabled />
					</span>
				</TooltipTrigger>
				<TooltipContent>Only admins can enable or disable alarms</TooltipContent>
			</Tooltip>
		);
	}

	return <Switch checked={checked} onCheckedChange={handleChange} />;
});
